import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowUpRight, ChevronDown, Eye, Pen, Square, Trash2, Star, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortIcon } from '@/components/common/SortIcon';
import { NEXT_PHASE } from '../types';
import type { Matter } from '../types';
import {
  getComplianceStatusColor,
  getMatterPhaseColor,
  getMatterResultColor,
} from '../statusStyles';

interface MatterColumnsProps {
  onEdit?: ((matter: Matter) => void) | undefined;
  onDelete?: ((matter: Matter) => void) | undefined;
  onNps?: ((matter: Matter) => void) | undefined;
  onRateTeam?: ((matter: Matter) => void) | undefined;
  /** Open the Quality Gate dialog for the matter's next lifecycle move. */
  onAdvance?: ((matter: Matter) => void) | undefined;
  hasAccess?: ((resource: string, action: string) => boolean) | undefined;
  orderBy?: string | undefined;
  order?: 'asc' | 'desc' | undefined;
  onSort?: ((column: string) => void) | undefined;
}

export function useMatterColumns({
  onEdit,
  onDelete,
  onNps,
  onRateTeam,
  onAdvance,
  hasAccess = () => true,
  orderBy,
  order,
  onSort,
}: MatterColumnsProps = {}): ColumnDef<Matter>[] {
  const { t } = useTranslation();

  const columns: ColumnDef<Matter>[] = [
    {
      accessorKey: 'name',
      header: () => <p>{t('name')}</p>,
      cell: ({ row }) => {
        const matter = row.original;
        const name = matter.name || matter.caseNumber || `#${matter.id}`;
        return (
          <div className="max-w-[20rem]">
            <Link
              to={`/app/matters/${matter.id}`}
              className="text-blue-700 hover:underline !whitespace-normal break-words"
            >
              {name}
            </Link>
            <p className="truncate" title={matter.description || undefined}>
              {matter.description || '-'}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: 'contract',
      header: () => <p>{t('contract')}</p>,
      cell: ({ row }) => {
        const contract = row.original.contract;
        if (!contract) return '-';
        return (
          <Link
            to={`/app/contracts/${contract.id}`}
            className="text-blue-700 hover:underline"
          >
            {contract.name}
          </Link>
        );
      },
    },
    {
      accessorKey: 'clientName',
      header: () => <p>{t('clientName')}</p>,
      cell: ({ row }) => {
        const client = row.original.contract?.client;
        if (!client) return '-';
        return (
          <p>
            {client.name || ''} {client.lastname || ''} {client.middlename || ''}
          </p>
        );
      },
    },
    {
      accessorKey: 'clientType',
      header: () => <p>{t('clientType')}</p>,
      cell: ({ row }) => {
        const client = row.original.contract?.client;
        if (!client?.type) return <p>-</p>;
        return <p>{client.type === 'PERSON' ? t('naturalPerson') : t('legalEntity')}</p>;
      },
    },
    // The legacy Status column is gone — the lifecycle phase is the truth.
    {
      accessorKey: 'phase',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('phase')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('phase')}
        >
          <p>{t('phase.label')}</p>
          <SortIcon active={orderBy === 'phase'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const phase = row.original.phase;
        if (!phase) return <p>-</p>;
        return (
          <Badge className={`rounded-full ${getMatterPhaseColor(phase)}`}>
            {t(`phase.${phase}`)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'category',
      header: () => <p>{t('matterCategory.label')}</p>,
      cell: ({ row }) => {
        const category = row.original.category;
        if (!category || category === 'MIGRATION_PENDING') return <p>-</p>;
        return (
          <Badge variant="outline" className="rounded-full text-xs">
            {t(`matterCategory.${category}`)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'compliance',
      header: () => <p>{t('compliance.label')}</p>,
      cell: ({ row }) => {
        const compliance = row.original.compliance;
        if (!compliance) return <p>-</p>;
        return (
          <Badge className={`rounded-full ${getComplianceStatusColor(compliance)}`}>
            {t(`compliance.status.${compliance}`)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'riskLevel',
      header: () => <p>{t('riskLevel')}</p>,
      cell: ({ row }) => {
        const riskLevel = row.original.riskLevel;
        if (riskLevel == null || riskLevel === '') return <p>-</p>;

        const value = parseFloat(riskLevel);
        if (isNaN(value)) return <p>{riskLevel}</p>;

        const badgeColor =
          value <= 10
            ? 'bg-green-500 text-white'
            : value <= 50
              ? 'bg-yellow-500 text-white'
              : 'bg-red-500 text-white';

        return (
          <Badge className={`rounded-full ${badgeColor}`}>
            {value}%
          </Badge>
        );
      },
    },
    {
      accessorKey: 'nps',
      header: () => <p>{t('nps.nps')}</p>,
      cell: ({ row }) => {
        const matter = row.original;
        const nps = matter.nps;

        if (nps == null) {
          if (matter.phase === 'CLOSED') {
            return (
              <button
                type="button"
                onClick={() => onNps?.(matter)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors whitespace-nowrap"
              >
                <Star className="h-3 w-3" />
                {t('nps.rateNps')}
              </button>
            );
          }
          return <span className="text-muted-foreground">—</span>;
        }

        const isPromoter = nps >= 9;
        const isPassive = nps >= 7;
        const badgeClass = isPromoter
          ? 'bg-green-100 text-green-700 border-green-200'
          : isPassive
            ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
            : 'bg-red-100 text-red-700 border-red-200';
        const label = isPromoter
          ? t('nps.promoter')
          : isPassive
            ? t('nps.passive')
            : t('nps.detractor');

        return (
          <Badge className={`rounded-full border text-xs ${badgeClass}`}>
            {nps} — {label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'result',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('result')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('result')}
        >
          <p>{t('result.result')}</p>
          <SortIcon active={orderBy === 'result'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const matter = row.original;
        const result = matter.result;

        if (!result) {
          return <Badge className="rounded-full bg-gray-300">----</Badge>;
        }

        // Different labels for court vs non-court cases
        let label: string;
        if (!matter.isCourtCase) {
          switch (result) {
            case 'WIN':
              label = t('success');
              break;
            case 'LOSE':
              label = t('failed');
              break;
            default:
              label = t(`result.${result}`);
          }
        } else {
          label = t(`result.${result}`);
        }

        return (
          <Badge className={`rounded-full ${getMatterResultColor(result)}`}>
            {label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'isCourtCase',
      header: () => <p>{t('legalCase')}</p>,
      cell: ({ row }) => {
        const isCourtCase = row.original.isCourtCase;
        return (
          <Badge
            className={`rounded-full px-2 py-1 ${
              isCourtCase
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {isCourtCase ? t('legalCase') : t('project')}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const date = row.original.createdAt;
        return date ? format(new Date(date), 'yyyy-MM-dd HH:mm') : '-';
      },
    },
  ];

  // Actions column: an Eye (view) button for everyone; everything else lives
  // in the dropdown. The lifecycle move mirrors the detail page — it opens the
  // Quality Gate dialog (quick matters → close gate, PROVISIONAL_OPEN →
  // emergency resolve, else advance to the legal next phase).
  columns.splice(0, 0, {
    id: 'actions',
    header: t('actionsLabel'),
    cell: ({ row }) => {
      const matter = row.original;
      const canEdit = hasAccess('matter', 'update');
      const canDelete = hasAccess('matter', 'delete');
      const canClose = hasAccess('matter', 'close');
      const canTransition = hasAccess('matter', 'transition');
      const showNps = matter.phase === 'CLOSED' && matter.nps == null && canClose;
      const showRateTeam =
        matter.phase === 'CLOSED' &&
        matter.nps != null &&
        canClose &&
        [...(matter.partners ?? []), ...(matter.responsibleAttorneys ?? []), ...(matter.staffs ?? [])].some(
          (u) => u?.avg_rating == null,
        );

      // Next lifecycle action for the row's current phase (detail-page rules).
      const isQuickMatter =
        matter.category === 'QA' && !!matter.quickDeadline && matter.phase !== 'CLOSED';
      const nextPhase = matter.phase ? NEXT_PHASE[matter.phase] : undefined;
      let advanceLabel: string | null = null;
      let AdvanceIcon = ArrowUpRight;
      if (isQuickMatter) {
        if (canClose && canTransition && !matter.result) {
          advanceLabel = t('closeMatter');
          AdvanceIcon = Square;
        }
      } else if (matter.phase === 'PROVISIONAL_OPEN') {
        if (canTransition) advanceLabel = t('emergency.resolveButton');
      } else if (
        nextPhase &&
        canTransition &&
        (nextPhase !== 'CLOSED' || canClose)
      ) {
        advanceLabel = t('gate.advanceTo', { phase: t(`phase.${nextPhase}`) });
      }

      const hasMenu =
        canEdit || canDelete || showNps || showRateTeam || !!advanceLabel;

      return (
        <div className="flex">
          <Link to={`/app/matters/${matter.id}`} title={t('view')}>
            <Button
              variant="outline"
              size="sm"
              className={hasMenu ? 'rounded-r-none' : undefined}
            >
              <Eye size={16} />
            </Button>
          </Link>

          {hasMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-l-none border-l-0 hover:bg-muted"
                >
                  <ChevronDown size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[150px] flex flex-col gap-1">
                {advanceLabel && (
                  <DropdownMenuItem onClick={() => onAdvance?.(matter)}>
                    <AdvanceIcon className="mr-2 h-4 w-4" />
                    {advanceLabel}
                  </DropdownMenuItem>
                )}

                {canEdit && (
                  <DropdownMenuItem onClick={() => onEdit?.(matter)}>
                    <Pen className="mr-2 h-4 w-4" />
                    {t('edit')}
                  </DropdownMenuItem>
                )}

                {showNps && (
                  <DropdownMenuItem onClick={() => onNps?.(matter)}>
                    <Star className="mr-2 h-4 w-4" />
                    {t('nps.rateNps')}
                  </DropdownMenuItem>
                )}

                {showRateTeam && (
                  <DropdownMenuItem onClick={() => onRateTeam?.(matter)}>
                    <UserCheck className="mr-2 h-4 w-4" />
                    {t('rateTeam')}
                  </DropdownMenuItem>
                )}

                {canDelete && (
                  <DropdownMenuItem
                    onClick={() => onDelete?.(matter)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      );
    },
  });

  return columns;
}
